import React, { useState, useEffect } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Wifi, Cpu, Camera, Mic, Loader2, Play, RotateCcw } from 'lucide-react';
import { usePreflightTest, PreflightTestResult } from '@/hooks/video-call/usePreflightTest';
import { usePreflightTestStrict } from '@/hooks/video-call/usePreflightTestStrict';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';

export const PreflightTestPage: React.FC = () => {
  const { toast } = useToast();
  const [useStrictMode, setUseStrictMode] = useState(false);

  // Use different hooks based on mode
  const normalTest = usePreflightTest();
  const strictTest = usePreflightTestStrict();

  const { runPreflightTest, cancelTest, resetTest, testProgress, testResult, isRunning } =
    useStrictMode ? strictTest : normalTest;

  // Test connection details - you'll need to get these from your backend
  const [serverUrl, setServerUrl] = useState('');
  const [token, setToken] = useState('');
  const [customServerUrl, setCustomServerUrl] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  // Get test connection details from backend
  const getTestConnectionDetails = async () => {
    try {
      const callServiceUrl = import.meta.env.VITE_CALL_SERVICE_URL || "http://localhost:8000";
      const response = await fetch(`${callServiceUrl}/api/initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          is_web_session: true,
          is_video: true,
          is_preflight_test: true,
          lead_id: "test-lead",
          assistant_id: "test-assistant",
          organization_id: "test-org",
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setServerUrl(data.livekit_url || '');
        setToken(data.livekit_token || '');
        return true;
      } else {
        throw new Error(`Failed to get connection details: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to get test connection details:', error);
      toast({
        title: "Connection Error",
        description: "Failed to get test connection details. Using manual entry.",
        variant: "destructive",
      });
      setUseCustom(true);
      return false;
    }
  };

  useEffect(() => {
    getTestConnectionDetails();
  }, []);

  const handleRunTest = async () => {
    const testUrl = useCustom ? customServerUrl : serverUrl;

    if (!testUrl || !token) {
      toast({
        title: "Missing Details",
        description: "Please provide server URL and token to run the test.",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await runPreflightTest(testUrl, token);

      // Show result in toast
      if (result.overall === 'pass') {
        toast({
          title: "✅ System Check Passed!",
          description: "Your system is ready for video interviews.",
        });
      } else if (result.overall === 'warning') {
        toast({
          title: "⚠️ System Check Warning",
          description: "Your system may experience some issues. Check recommendations below.",
          variant: "default",
        });
      } else {
        toast({
          title: "❌ System Check Failed",
          description: "Your system doesn't meet minimum requirements. See details below.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Test failed:', error);
    }
  };

  const handleReset = () => {
    resetTest();
    setServerUrl('');
    setToken('');
    getTestConnectionDetails();
  };

  const getProgressIcon = () => {
    switch (testProgress.stage) {
      case 'devices':
        return <Camera className="w-6 h-6 text-foreground animate-pulse" />;
      case 'network':
        return <Wifi className="w-6 h-6 text-foreground animate-pulse" />;
      case 'connection':
      case 'dataChannel':
        return <Cpu className="w-6 h-6 text-foreground animate-pulse" />;
      default:
        return <Loader2 className="w-6 h-6 text-foreground animate-spin" />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-good';
    if (score >= 50) return 'text-mid';
    return 'text-danger';
  };

  const getOverallBadge = (overall: PreflightTestResult['overall']) => {
    switch (overall) {
      case 'pass':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-good-bg text-good">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            PASS
          </span>
        );
      case 'warning':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-mid-bg text-mid">
            <AlertCircle className="w-4 h-4 mr-1" />
            WARNING
          </span>
        );
      case 'fail':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-danger-bg text-danger">
            <XCircle className="w-4 h-4 mr-1" />
            FAIL
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-muted p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-card rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold mb-4">Pre-flight System Check Test Page</h1>
          <p className="text-muted-foreground mb-6">
            This page tests the pre-flight check system that verifies if a user's system can handle video interviews.
          </p>

          {/* Mode Toggle */}
          <div className="mb-6 p-4 border rounded-lg bg-mid-bg border-mid-line">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium">Test Mode</h3>
              <div className="inline-flex items-center space-x-2 bg-card rounded-lg p-1 shadow-sm">
                <button
                  onClick={() => setUseStrictMode(false)}
                  className={`px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                    !useStrictMode
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Normal
                </button>
                <button
                  onClick={() => setUseStrictMode(true)}
                  className={`px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                    useStrictMode
                      ? 'bg-destructive text-white'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Strict (Fail Test)
                </button>
              </div>
            </div>
            {useStrictMode && (
              <div className="text-danger text-sm">
                ⚠️ <strong>Strict Mode Active:</strong> This mode has extremely high thresholds designed to fail for testing purposes:
                <ul className="mt-1 ml-6 list-disc">
                  <li>Requires 12+ CPU cores (high), 8+ cores (medium)</li>
                  <li>Requires 8GB+ RAM</li>
                  <li>Requires 5+ Mbps bandwidth</li>
                  <li>Requires &lt;50ms latency</li>
                  <li>50% chance of simulated DataChannel failure</li>
                  <li>Overall pass score: 85+ (vs 70+ in normal mode)</li>
                </ul>
              </div>
            )}
            {!useStrictMode && (
              <div className="text-good text-sm">
                ✓ <strong>Normal Mode:</strong> Production thresholds for real-world usage
              </div>
            )}
          </div>

          {/* Connection Settings */}
          <div className="border rounded-lg p-4 mb-6 bg-muted">
            <h3 className="font-medium mb-3">Connection Settings</h3>

            <div className="mb-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={useCustom}
                  onChange={(e) => setUseCustom(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Use custom server URL</span>
              </label>
            </div>

            {useCustom ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">LiveKit Server URL</label>
                  <input
                    type="text"
                    value={customServerUrl}
                    onChange={(e) => setCustomServerUrl(e.target.value)}
                    placeholder="wss://your-livekit-server.com"
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Token</label>
                  <textarea
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="Paste your LiveKit token here"
                    className="w-full px-3 py-2 border rounded-md h-20 font-mono text-xs"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-medium">Server URL:</span>{' '}
                  <code className="bg-muted px-2 py-1 rounded">
                    {serverUrl || 'Fetching...'}
                  </code>
                </p>
                <p>
                  <span className="font-medium">Token:</span>{' '}
                  <code className="bg-muted px-2 py-1 rounded">
                    {token ? `${token.substring(0, 20)}...` : 'Fetching...'}
                  </code>
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleRunTest}
              disabled={isRunning || (!useCustom && (!serverUrl || !token))}
              className="flex-1"
            >
              {isRunning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Running Test...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Run Pre-flight Test
                </>
              )}
            </Button>

            {isRunning && (
              <Button
                onClick={cancelTest}
                variant="outline"
                className="px-6"
              >
                Cancel
              </Button>
            )}

            {testResult && !isRunning && (
              <Button
                onClick={handleReset}
                variant="outline"
                className="px-6"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            )}
          </div>
        </div>

        {/* Test Progress */}
        {isRunning && !testResult && (
          <div className="bg-card rounded-lg shadow-lg p-6 mb-6">
            <div className="flex items-center space-x-4">
              {getProgressIcon()}
              <div className="flex-1">
                <div className="text-lg font-medium">{testProgress.message}</div>
                <div className="mt-2">
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-500"
                      style={{ width: `${testProgress.progress}%` }}
                    />
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">{testProgress.progress}% complete</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Test Results */}
        {testResult && !isRunning && (
          <div className="space-y-6">
            {/* Overall Result */}
            <div className="bg-card rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Test Results</h2>
                {getOverallBadge(testResult.overall)}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Network Score */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Wifi className="w-5 h-5 text-muted-foreground" />
                      <span className="font-medium">Network</span>
                    </div>
                    <span className={`font-bold text-lg ${getScoreColor(testResult.network.score)}`}>
                      {testResult.network.score}/100
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Bandwidth:</span>
                      <span className="font-medium">{testResult.network.bandwidth.toFixed(1)} Mbps</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Latency:</span>
                      <span className="font-medium">{testResult.network.latency}ms</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Packet Loss:</span>
                      <span className="font-medium">{testResult.network.packetLoss}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Jitter:</span>
                      <span className="font-medium">{testResult.network.jitter}ms</span>
                    </div>
                  </div>
                </div>

                {/* Hardware Score */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Cpu className="w-5 h-5 text-muted-foreground" />
                      <span className="font-medium">Hardware</span>
                    </div>
                    <span className={`font-bold text-lg ${getScoreColor(testResult.hardware.score)}`}>
                      {testResult.hardware.score}/100
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">CPU:</span>
                      <span className="font-medium capitalize">{testResult.hardware.cpu}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Memory:</span>
                      <span className="font-medium">{testResult.hardware.memory}GB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Browser:</span>
                      <span className="font-medium">{testResult.hardware.browser}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">WebRTC:</span>
                      <span className="font-medium">
                        {testResult.hardware.webrtcSupport ? (
                          <CheckCircle2 className="w-4 h-4 text-good inline" />
                        ) : (
                          <XCircle className="w-4 h-4 text-danger inline" />
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Devices */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Camera className="w-5 h-5 text-muted-foreground" />
                    <span className="font-medium">Devices</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Camera:</span>
                      {testResult.devices.camera ? (
                        <CheckCircle2 className="w-5 h-5 text-good" />
                      ) : (
                        <XCircle className="w-5 h-5 text-danger" />
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Microphone:</span>
                      {testResult.devices.microphone ? (
                        <CheckCircle2 className="w-5 h-5 text-good" />
                      ) : (
                        <XCircle className="w-5 h-5 text-danger" />
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Speaker:</span>
                      {testResult.devices.speaker ? (
                        <CheckCircle2 className="w-5 h-5 text-good" />
                      ) : (
                        <XCircle className="w-5 h-5 text-danger" />
                      )}
                    </div>
                  </div>
                </div>

                {/* DataChannel Status (Critical) */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Mic className="w-5 h-5 text-muted-foreground" />
                    <span className="font-medium">Data Channels</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Reliable Channel:</span>
                      {testResult.dataChannel.reliable ? (
                        <CheckCircle2 className="w-5 h-5 text-good" />
                      ) : (
                        <XCircle className="w-5 h-5 text-danger" />
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Lossy Channel:</span>
                      {testResult.dataChannel.lossy ? (
                        <CheckCircle2 className="w-5 h-5 text-good" />
                      ) : (
                        <XCircle className="w-5 h-5 text-danger" />
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Bidirectional:</span>
                      {testResult.dataChannel.bidirectional ? (
                        <CheckCircle2 className="w-5 h-5 text-good" />
                      ) : (
                        <XCircle className="w-5 h-5 text-danger" />
                      )}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Latency:</span>
                      <span className="font-medium">{testResult.dataChannel.latency}ms</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              {testResult.recommendations.length > 0 && (
                <div className="mt-6 border-t pt-4">
                  <h3 className="font-medium mb-3 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 text-mid" />
                    Recommendations
                  </h3>
                  <ul className="space-y-2">
                    {testResult.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start text-sm">
                        <span className="text-mid mr-2 mt-0.5">•</span>
                        <span className="text-foreground">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Can Proceed Status */}
              <div className="mt-6 p-4 rounded-lg bg-muted">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Can Proceed with Interview:</span>
                  {testResult.canProceed ? (
                    <span className="flex items-center text-good font-medium">
                      <CheckCircle2 className="w-5 h-5 mr-1" />
                      YES
                    </span>
                  ) : (
                    <span className="flex items-center text-danger font-medium">
                      <XCircle className="w-5 h-5 mr-1" />
                      NO
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};